import { useState, useEffect, useMemo } from "preact/hooks";
import { getL11nString } from "@/l11n/l11n";
import PrivacyPolicy from "@/ui/components/PrivacyPolicy";
import ContactUs from "@/ui/components/ContactUs";
import StagingConfig from "@/ui/components/StagingConfig";
import { isStagingHost, isDevHost } from "@/lib/web";
import { logout } from "@/lib/api_controller";

export default function HamburgerMenu() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [contactModalIsOpen, setContactModalIsOpen] = useState(false);
  const [privacyPolicyIsOpen, setPrivacyPolicyIsOpen] = useState(false);
  const [langPickerIsOpen, setLangPickerIsOpen] = useState(false);

  const strings = useMemo(
    () => ({
      taaliaHome: getL11nString("taalia_home"),
      contactUs: getL11nString("contact_us"),
      copyright: getL11nString("copyright"),
      close: getL11nString("close"),
      privacyPolicy: getL11nString("privacy_policy"),
      termsOfService: getL11nString("terms_of_service"),
      setLanguages: "Test Config",
      reset: "Reset",
    }),
    []
  );

  const toggleMenu = () => setMenuOpen(!menuOpen);

  const handleModalClose = () => {
    setContactModalIsOpen(false);
    setPrivacyPolicyIsOpen(false);
    setLangPickerIsOpen(false);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuOpen && !event.target.closest(".menu-container")) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  return (
    <div className="menu-container relative z-10">
      <button
        onClick={toggleMenu}
        className="ml-1 mt-1 p-0 rounded-md hover:bg-gray-100 transition-colors bg-white"
        aria-label="Menu"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 6h16M4 12h16M4 18h16"
          />
        </svg>
      </button>

      {menuOpen && (
        <div className="absolute top-full left-0 mt-1 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
          <div className="py-1" role="menu" aria-orientation="vertical">
            <a
              href="https://www.taalia.com"
              target="_blank"
              rel="noreferrer"
              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              role="menuitem"
              onClick={() => setMenuOpen(false)}
            >
              {strings.taaliaHome}
            </a>
            <a
              href="#"
              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                setContactModalIsOpen(true);
              }}
            >
              {strings.contactUs}
            </a>

            {/* Only show Set Languages option on dev & staging hosts */}
            {(isStagingHost || isDevHost) && (
              <a
                href="#"
                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false);
                  setLangPickerIsOpen(true);
                }}
              >
                {strings.setLanguages}
              </a>
            )}

            {/* Only show Reset option on dev & staging hosts */}
            {(isStagingHost || isDevHost) && (
              <a
                href="#"
                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false);
                  logout();
                }}
              >
                {strings.reset}
              </a>
            )}

            <hr className="mt-2 mb-0" />
            <div className="text-xs text-gray-700 p-2">{strings.copyright}</div>
            <div className="flex justify-center space-x-3 mb-2">
              <a
                onClick={() => {
                  setMenuOpen(false);
                  setPrivacyPolicyIsOpen(true);
                }}
                className="text-blue-600 hover:text-blue-800 hover:underline transition-colors cursor-pointer text-xs"
              >
                {strings.privacyPolicy}
              </a>
              <a
                href="https://www.taalia.com/legal/terms-of-service"
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 hover:text-blue-800 hover:underline transition-colors text-xs"
              >
                {strings.termsOfService}
              </a>
            </div>
          </div>
        </div>
      )}

      {contactModalIsOpen && (
        <Modal onClose={handleModalClose}>
          <ContactUs />
          <button className="text-white bg-blue-500 mt-3" onClick={handleModalClose}>
            {strings.close}
          </button>
        </Modal>
      )}

      {privacyPolicyIsOpen && (
        // <Modal onClose={handleModalClose}>
        //   <div className="flex-1 overflow-auto">
        //     <PrivacyPolicy />
        //   </div>
        //   <button className="text-white bg-blue-500 mt-3" onClick={handleModalClose}>
        //     {strings.close}
        //   </button>
        // </Modal>
        <div
          className="modal fixed inset-0 flex items-center justify-center px-4 bg-black bg-opacity-50"
          onClick={() => handleModalClose(false)}
        >
          <div
            className="modal-content bg-white text-black p-4 text-lg rounded w-full max-w-lg h-[90vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Ensure PrivacyPolicy expands */}
            <div className="flex-1 overflow-auto">
              <PrivacyPolicy />
            </div>

            <button
              className="text-white bg-blue-500 mt-3 p-2 rounded"
              onClick={() => setPrivacyPolicyIsOpen(false)}
            >
              {strings.close}
            </button>
          </div>
        </div>
      )}

      {langPickerIsOpen && (
        <Modal onClose={handleModalClose}>
          <StagingConfig onCancel={handleModalClose} />
        </Modal>
      )}
    </div>
  );
}

// Generic Modal Component for reuse
function Modal({ onClose, children }) {
  return (
    <div
      className="modal fixed inset-0 flex items-center justify-center bg-black bg-opacity-50"
      onClick={onClose}
    >
      <div
        className="modal-content bg-white text-black p-4 text-lg rounded mx-6"
        style={{ maxWidth: "26rem" }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}
